import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, getFirestore } from 'firebase/firestore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faPenToSquare
} from '@fortawesome/free-solid-svg-icons';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import { app } from '../../firebase';
import '../dash.css';
import { formatDateTime, formatKes, formatShortDate, getMonthLabel } from '../../utils/financeWorkflow';

const SECTION_CONFIG = {
  payments: {
    title: 'Vendor payments',
    eyebrow: 'Vendor management',
    description: 'Track every payment made to this vendor, including method, period, and reference details.',
    emptyMessage: 'No vendor payments have been recorded yet.',
    searchPlaceholder: 'Search amount, method, reference, or status...'
  },
  changeLogs: {
    title: 'Change logs',
    eyebrow: 'Vendor audit trail',
    description: 'Review vendor edits, deletes, and related admin activity in a simple log table.',
    emptyMessage: 'No vendor change logs found yet.',
    searchPlaceholder: 'Search action, admin, or details...'
  },
  relatedBills: {
    title: 'Related bills',
    eyebrow: 'Vendor billing',
    description: 'See the bills linked to this vendor and follow what still needs to be settled.',
    emptyMessage: 'No bills are linked to this vendor yet.',
    searchPlaceholder: 'Search bill, period, amount, or due date...'
  }
};

const SECTION_BUTTONS = [
  { key: 'payments', label: 'View vendor payments', path: 'payments' },
  { key: 'changeLogs', label: 'Change logs', path: 'change-logs' },
  { key: 'relatedBills', label: 'Related bills', path: 'related-bills' }
];

const toDateObject = (value) => {
  if (!value) return null;
  if (value?.toDate) return value.toDate();
  if (value?.seconds) return new Date(value.seconds * 1000);
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const toTimestampNumber = (value) => toDateObject(value)?.getTime() || 0;

const toSearchBlob = (values) => values.filter(Boolean).join(' ').toLowerCase();

function VendorSectionComp({ section }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const firestore = getFirestore(app);

  const config = SECTION_CONFIG[section];
  const [vendor, setVendor] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const loadSection = useCallback(async () => {
    if (!id || !config) {
      setVendor(null);
      setRows([]);
      return;
    }

    const vendorSnap = await getDoc(doc(firestore, 'Vendors', id));
    if (!vendorSnap.exists()) {
      setVendor(null);
      setRows([]);
      return;
    }

    const vendorRecord = { id: vendorSnap.id, ...vendorSnap.data() };
    setVendor(vendorRecord);

    const [billSnap, paymentSnap, activitySnap] = await Promise.all([
      getDocs(collection(firestore, 'Bills')),
      getDocs(collection(firestore, 'BillPayments')),
      getDocs(collection(firestore, 'AdminActivity'))
    ]);

    const bills = billSnap.docs.map((item) => ({ id: item.id, ...item.data() }));
    const payments = paymentSnap.docs.map((item) => ({ id: item.id, ...item.data() }));
    const activities = activitySnap.docs.map((item) => ({ id: item.id, ...item.data() }));

    if (section === 'payments') {
      const vendorName = String(vendorRecord.vendorName || '').trim().toLowerCase();
      const paymentRows = payments
        .filter((payment) => {
          const paymentVendorName = String(payment.vendorName || payment.payee || '').trim().toLowerCase();
          return payment.vendorId === id || (vendorName && paymentVendorName === vendorName);
        })
        .sort(
          (first, second) =>
            toTimestampNumber(second.paymentDate || second.createdAt) - toTimestampNumber(first.paymentDate || first.createdAt)
        )
        .map((payment) => ({
          id: payment.id,
          title: payment.billTitle || payment.billName || payment.reference || 'Vendor payment',
          amount: payment.amount,
          paidOn: payment.paymentDate || payment.createdAt,
          periodLabel: payment.billingPeriodLabel || (payment.billingPeriodKey ? getMonthLabel(payment.billingPeriodKey) : '—'),
          method: payment.paymentMethod || 'Not set',
          reference: payment.reference || payment.transactionId || '—',
          status: payment.paymentStatus || payment.status || 'Pending',
          notes: payment.notes || ''
        }));

      setRows(paymentRows);
      return;
    }

    if (section === 'changeLogs') {
      const vendorName = String(vendorRecord.vendorName || '').trim().toLowerCase();
      const logRows = activities
        .filter((entry) => {
          const actionBlob = toSearchBlob([
            entry.action,
            entry.details,
            entry.adminName,
            entry.vendorName,
            entry.vendorSection
          ]);
          return entry.vendorId === id || (vendorName && actionBlob.includes(vendorName));
        })
        .sort((first, second) => toTimestampNumber(second.createdAt) - toTimestampNumber(first.createdAt))
        .map((entry) => ({
          id: entry.id,
          createdAt: entry.createdAt,
          action: entry.action || 'Activity',
          details: entry.details || '',
          adminName: entry.adminName || 'Admin'
        }));

      setRows(logRows);
      return;
    }

    if (section === 'relatedBills') {
      const billRows = bills
        .filter((bill) => bill.vendor === id || bill.vendorId === id)
        .sort(
          (first, second) =>
            toTimestampNumber(second.createdAt || second.timeCreated) - toTimestampNumber(first.createdAt || first.timeCreated)
        )
        .map((bill) => ({
          id: bill.id,
          title: bill.billTitle || bill.title || 'Bill',
          amount: bill.billAmount || bill.amount || 0,
          dueDate: bill.billDueDate || bill.dueDate || bill.createdAt || null,
          periodLabel: bill.billingPeriodLabel || (bill.billingPeriodKey ? getMonthLabel(bill.billingPeriodKey) : '—'),
          notes: bill.billNotes || bill.notes || '',
          status: bill.billStatus || bill.status || 'Open'
        }));

      setRows(billRows);
      return;
    }

    setRows([]);
  }, [config, firestore, id, section]);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        setLoading(true);
        await loadSection();
      } catch (error) {
        console.error('Failed to load vendor section:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPage();
  }, [loadSection]);

  const filteredRows = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    if (!normalizedSearch) {
      return rows;
    }

    return rows.filter((row) => {
      const blob = toSearchBlob([
        row.title,
        row.action,
        row.details,
        row.adminName,
        row.method,
        row.reference,
        row.status,
        row.periodLabel,
        row.notes,
        formatKes(row.amount),
        formatShortDate(row.paidOn || row.createdAt),
        formatShortDate(row.dueDate)
      ]);
      return blob.includes(normalizedSearch);
    });
  }, [rows, searchTerm]);

  const headerStats = useMemo(() => {
    const totalAmount = filteredRows.reduce((sum, row) => sum + Number(row.amount || 0), 0);
    return {
      count: filteredRows.length,
      totalAmount
    };
  }, [filteredRows]);

  if (loading) {
    return (
      <div className="clientsComp applicationsPage">
        <div className="appsTableCard">
          <div className="opsLoadingCard">
            <Spinner animation="border" role="status" />
            <span>Loading vendor records...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!vendor || !config) {
    return (
      <div className="clientsComp applicationsPage">
        <div className="appsTableCard">
          <div className="opsEmptyState">
            <h6>Vendor section not found</h6>
            <p>The vendor profile or section you requested could not be loaded.</p>
            <button type="button" className="opsGhostBtn" onClick={() => navigate(`/view-vendor/${id}`)}>
              <FontAwesomeIcon icon={faArrowLeft} /> Back to vendor
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="clientsComp applicationsPage vendorSectionPage">
      <div className="appsHeader">
        <div>
          <button type="button" className="opsGhostBtn mb-3" onClick={() => navigate(`/view-vendor/${id}`)}>
            <FontAwesomeIcon icon={faArrowLeft} /> Back to vendor
          </button>
          <p className="appsKicker">{config.eyebrow}</p>
          <h4>{config.title}</h4>
          <p className="appsSubtext">{config.description}</p>
        </div>
        <div className="appsHeaderStats">
          <div className="appsStatChip">
            <span>Records</span>
            <strong>{headerStats.count}</strong>
          </div>
          <div className="appsStatChip">
            <span>Total</span>
            <strong>{formatKes(headerStats.totalAmount)}</strong>
          </div>
          <div className="appsStatChip">
            <span>Vendor</span>
            <strong>{vendor.vendorName || 'Vendor'}</strong>
          </div>
        </div>
      </div>

      <div className="appsFiltersCard vendorSectionNavCard">
        <div className="appsFilterGroup">
          {SECTION_BUTTONS.map((button) => (
            <Button
              key={button.key}
              variant={button.key === section ? 'dark' : 'outline-dark'}
              onClick={() => navigate(`/view-vendor/${id}/${button.path}`)}
            >
              {button.label}
            </Button>
          ))}
        </div>
        <div className="appsSearch input-group vendorSectionSearch">
          <span className="input-group-text">
            <FontAwesomeIcon icon={faPenToSquare} />
          </span>
          <Form.Control
            type="text"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder={config.searchPlaceholder}
          />
        </div>
      </div>

      <div className="appsTableCard">
        <div className="appsTableHeader">
          <h6>{config.title}</h6>
          <span>{filteredRows.length} results</span>
        </div>
        <div className="appsTableWrap">
          {filteredRows.length === 0 ? (
            <div className="appsEmptyState">{config.emptyMessage}</div>
          ) : (
            <Table hover responsive className="appsTable">
              {section === 'changeLogs' ? (
                <>
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Action</th>
                      <th>Details</th>
                      <th>Admin</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((row) => (
                      <tr key={row.id}>
                        <td>{formatDateTime(row.createdAt, '—')}</td>
                        <td>{row.action}</td>
                        <td>{row.details || '—'}</td>
                        <td>{row.adminName || 'Admin'}</td>
                      </tr>
                    ))}
                  </tbody>
                </>
              ) : section === 'payments' ? (
                <>
                  <thead>
                    <tr>
                      <th>Payment</th>
                      <th>Amount</th>
                      <th>Paid on</th>
                      <th>Period</th>
                      <th>Method</th>
                      <th>Reference</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((row) => (
                      <tr key={row.id}>
                        <td>{row.title}</td>
                        <td>{formatKes(row.amount)}</td>
                        <td>{formatDateTime(row.paidOn, '—')}</td>
                        <td>{row.periodLabel || '—'}</td>
                        <td>{row.method || '—'}</td>
                        <td>{row.reference || '—'}</td>
                        <td>{row.status || 'Pending'}</td>
                      </tr>
                    ))}
                  </tbody>
                </>
              ) : (
                <>
                  <thead>
                    <tr>
                      <th>Bill</th>
                      <th>Amount</th>
                      <th>Period</th>
                      <th>Due date</th>
                      <th>Status</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((row) => (
                      <tr key={row.id}>
                        <td>{row.title}</td>
                        <td>{formatKes(row.amount)}</td>
                        <td>{row.periodLabel || '—'}</td>
                        <td>{formatShortDate(row.dueDate, '—')}</td>
                        <td>{row.status || 'Open'}</td>
                        <td>{row.notes || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </>
              )}
            </Table>
          )}
        </div>
      </div>
    </div>
  );
}

export default VendorSectionComp;
